import { useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";

import DeleteJobConfirmDialog from "components/botsManagement/DeleteJobConfirmDialog";
import { useDiscardPdfJobMutation, type PdfJobListItem } from "features/pdfTools/pdfTools";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { apiErrorText, isSplitJobSaved } from "./splitJobUi";

export interface SplitJobDeleteDialogProps {
  /** The job to delete; null keeps the dialog closed. */
  job: Pick<PdfJobListItem, "job_id" | "splitter_label" | "status" | "publish" | "user"> | null;
  onClose: () => void;
  onDeleted?: () => void;
}

/**
 * Delete a split job: its workspace goes (uploaded archives, results, error
 * pages) unless the operator keeps it; a result already saved to Data Storage
 * is the client's folder now and goes only when asked, with its name typed.
 */
export default function SplitJobDeleteDialog({
  job,
  onClose,
  onDeleted,
}: SplitJobDeleteDialogProps) {
  const dispatch = useAppDispatch();
  const [discard, { isLoading: busy }] = useDiscardPdfJobMutation();
  const [deleteWorkspace, setDeleteWorkspace] = useState(true);
  const [deleteSaved, setDeleteSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDeleteWorkspace(true);
    setDeleteSaved(false);
    setError(null);
  }, [job?.job_id]);

  const saved = job != null && isSplitJobSaved(job);
  const savedFolder = job?.publish.folder_name ?? "";

  const confirm = async () => {
    if (!job) return;
    setError(null);
    try {
      await discard({
        jobId: job.job_id,
        userId: job.user.id,
        delete_data: deleteWorkspace,
        delete_saved_data: saved && deleteSaved,
      }).unwrap();
      dispatch(pushNotification({ message: "Split job deleted.", severity: "success" }));
      onClose();
      onDeleted?.();
    } catch (err: unknown) {
      setError(apiErrorText(err, "The split job could not be deleted."));
    }
  };

  return (
    <DeleteJobConfirmDialog
      open={job != null}
      title="Delete split job"
      message={
        <>
          Delete the split job <b>{job?.splitter_label}</b>? It leaves the major job&apos;s
          list.
        </>
      }
      detail={null}
      extra={
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={deleteWorkspace}
                disabled={busy}
                onChange={(e) => setDeleteWorkspace(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Also delete its workspace in S3 (uploaded archives, results, error pages)
              </Typography>
            }
          />
          {saved && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="error"
                  checked={deleteSaved}
                  disabled={busy}
                  onChange={(e) => setDeleteSaved(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Also delete the folder saved to Data Storage ({job?.publish.destination}) -
                  the client&apos;s documents, upload jobs may read them
                </Typography>
              }
            />
          )}
        </Box>
      }
      confirmPhrase={saved && deleteSaved ? savedFolder : undefined}
      busy={busy}
      error={error}
      onCancel={onClose}
      onConfirm={() => void confirm()}
    />
  );
}
